import { supabase } from './supabase.js';

export const familyInvitationService = {
  // Create family invitation (single-family architecture)
  async createInvitation(invitedBy, inviteeData) {
    const { data, error } = await supabase
      .from('family_invitations')
      .insert([{
        
        invited_by: invitedBy,
        invitee_name: inviteeData.name,
        invitee_email: inviteeData.email,
        invitee_phone: inviteeData.phone,
        date_of_birth: inviteeData.dateOfBirth,
        role: inviteeData.role || 'child',
        existing_member_id: inviteeData.existingMemberId || null,
        family_name: inviteeData.familyName || 'Summer Planner'
      }])
      .select(`
        *,
        family_members!invited_by(name)
      `)
      .single();

    if (error) throw error;
    return data;
  },

  // Get invitation by code
  async getInvitationByCode(inviteCode) {
    const { data, error } = await supabase
      .from('family_invitations')
      .select(`
        *,
        family_members!invited_by(name)
      `)
      .ilike('invite_code', inviteCode)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Invalid or expired invitation code');
      }
      throw error;
    }

    return data;
  },

  // Accept invitation and create user account
  async acceptInvitation(inviteCode, userDetails) {
    try {
      // First, get the invitation
      const invitation = await this.getInvitationByCode(inviteCode);
      
      // Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userDetails.email,
        password: userDetails.password,
        options: {
          data: {
            
            invitation_id: invitation.id,
            name: invitation.invitee_name,
            role: invitation.role
          }
        }
      });

      if (authError) throw authError;

      // Calculate age from date of birth
      const age = invitation.date_of_birth 
        ? new Date().getFullYear() - new Date(invitation.date_of_birth).getFullYear()
        : null;

      let member;
      
      if (invitation.existing_member_id) {
        // Update existing family member record with auth user ID
        const { data: updatedMember, error: updateError } = await supabase
          .from('family_members')
          .update({
            auth_user_id: authData.user?.id,
            invitation_id: invitation.id
          })
          .eq('id', invitation.existing_member_id)
          .select()
          .single();

        if (updateError) throw updateError;
        member = updatedMember;
      } else {
        // Create new family member record
        const { data: newMember, error: memberError } = await supabase
          .from('family_members')
          .insert([{
            
            name: invitation.invitee_name,
            role: invitation.role,
            date_of_birth: invitation.date_of_birth,
            age: age,
            auth_user_id: authData.user?.id,
            invited_by: invitation.invited_by,
            invitation_id: invitation.id,
            avatar: invitation.role === 'child' ? '🧒' : '👩'
          }])
          .select()
          .single();

        if (memberError) throw memberError;
        member = newMember;
      }

      // Mark invitation as accepted
      const { error: updateError } = await supabase
        .from('family_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          accepted_by: authData.user?.id
        })
        .eq('id', invitation.id);

      if (updateError) throw updateError;

      return {
        user: authData.user,
        family_member: member
      };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  },

  // Get pending invitations for a family (single-family architecture)
  async getFamilyInvitations() {
    const { data, error } = await supabase
      .from('family_invitations')
      .select(`
        *,
        family_members!invited_by(name)
      `)
      // family_id filter removed for single-family architecture
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Cancel invitation
  async cancelInvitation(invitationId) {
    const { error } = await supabase
      .from('family_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) throw error;
  },

  // Resend invitation (generate new code and extend expiry)
  async resendInvitation(invitationId) {
    const { data, error } = await supabase
      .from('family_invitations')
      .update({
        invite_code: null, // Will trigger auto-generation
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
        status: 'pending'
      })
      .eq('id', invitationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Send invitation via SMS
  async sendInvitationSMS(invitation, familyName, inviterName) {
    try {
      const message = `Hi ${invitation.invitee_name}! ${inviterName} invited you to join the ${familyName} summer planner. Use code ${invitation.invite_code} to sign up at [app-link]. Code expires in 7 days.`;
      
      if (invitation.invitee_phone) {
        // This would integrate with your SMS service
        console.log('SMS invitation sent:', message);
        // await smsService.send(invitation.invitee_phone, message);
      }
      
      return true;
    } catch (error) {
      console.error('Error sending invitation SMS:', error);
      return false;
    }
  },

  // Send invitation via email
  async sendInvitationEmail(invitation, familyName, inviterName) {
    try {
      // This would integrate with your email service
      const subject = `You're invited to join ${familyName}'s summer planner!`;
      const body = `
        Hi ${invitation.invitee_name}!
        
        ${inviterName} has invited you to join the ${familyName} family summer planner.
        
        To accept this invitation:
        1. Go to [app-link]
        2. Click "Join Family"
        3. Enter invitation code: ${invitation.invite_code}
        4. Create your account
        
        This invitation expires in 7 days.
        
        Happy planning!
      `;
      
      console.log('Email invitation would be sent:', { subject, body });
      
      return true;
    } catch (error) {
      console.error('Error sending invitation email:', error);
      return false;
    }
  },

  // Cleanup expired invitations (run periodically)
  async cleanupExpiredInvitations() {
    const { error } = await supabase.rpc('cleanup_expired_invitations');
    if (error) throw error;
  }
};